import { useCallback, useEffect, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
const ROW_HEIGHT = 56;
const TABLE_HEADER = 34;
const MIN_ROWS = 5;
const MAX_ROWS = 100;
/**
 * This hook is in charge of handling the logic behind the number of rows
 * per page (RPP) in a given data table.
 *
 * There are three data sources used in determining what RPP to use:
 * - The dom rect height of the table
 * - The RPP stored in local storage
 * - The RPP stored in the URL query param
 *
 * In order of priority:
 * 1. URL query param (if the user A shares a link to user B, user B should
 * 		see exactly what user A sees)
 * 2. Local storage (if user C sets the RPP for customers table to 20, goes
 * 		to another page, then comes back to the customers table, it should
 * 		still be 20)
 * 3. The table height (the RPP is set to fit as many rows as possible without
 * 		overflowing the table)
 *
 * If the state value for a given data source is null/undefined, the RPP
 * used should be the next priority data source. EX: if there is no RPP
 * in the Url query param, the local storage RPP should be used.
 */
export function useRowsPerPage({
  itemType,
  rowHeight = ROW_HEIGHT,
  useCalculatedRowsPerPageState = useState
}) {
  const [preferredRowsPerPage, setPreferredRowsPerPage] = useLocalStorageState(`items-per-page:${itemType}`);
  const [calculatedRowsPerPage, setCalculatedRowsPerPage] = useCalculatedRowsPerPageState();
  const [tableWrapElement, tableWrapRef] = useState(null);
  useEffect(() => {
    // Don't try calculating rows per page when:
    // - the user has a specific preference
    if (typeof preferredRowsPerPage === 'number' && typeof calculatedRowsPerPage !== 'number') {
      setCalculatedRowsPerPage(preferredRowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedRowsPerPage]);
  useEffect(() => {
    // Don't try calculating rows per page when:
    // - the user has a specific preference
    // - we have already calculated rows per page
    if (typeof preferredRowsPerPage === 'number' || typeof calculatedRowsPerPage === 'number') {
      // We don't need to calculate ideal rows per page.
      return;
    }
    if (tableWrapElement === null) {
      // We don't have tableWrapElement so we cannot calculate ideal rows per page.
      return;
    }
    let animationHandle = undefined;
    const resizeObserver = new ResizeObserver(entries => {
      animationHandle = requestAnimationFrame(() => {
        for (const entry of entries) {
          // This height will account for the space the scroll bar at the bottom
          // takes up IF tableWrapRef is applied to the scrolling area.
          //
          // The table's header MUST have the same width as the table's rows so
          // that we can tell if there will be a horizontal scroll bar before
          // the table's rows are rendered.
          const tableHeight = entry.contentRect.height;
          if (tableHeight > 0) {
            const availableHeight = tableHeight - TABLE_HEADER;
            const newRowsPerPage = Math.min(Math.max(Math.floor(availableHeight / rowHeight), MIN_ROWS), MAX_ROWS);
            setCalculatedRowsPerPage(newRowsPerPage);
            return;
          }
        }
      });
    });
    resizeObserver.observe(tableWrapElement);
    return () => {
      if (animationHandle !== undefined) {
        cancelAnimationFrame(animationHandle);
      }
      resizeObserver.disconnect();
    };
  }, [calculatedRowsPerPage, rowHeight, preferredRowsPerPage, tableWrapElement, setCalculatedRowsPerPage]);
  return {
    rowsPerPage: calculatedRowsPerPage ?? null,
    setRowsPerPage: useCallback(newItemsPerPage => {
      if (typeof newItemsPerPage === 'number') {
        setPreferredRowsPerPage(newItemsPerPage);
        setCalculatedRowsPerPage(newItemsPerPage);
      } else {
        setPreferredRowsPerPage(undefined);
        setCalculatedRowsPerPage(undefined);
      }
    }, [setCalculatedRowsPerPage, setPreferredRowsPerPage]),
    tableWrapRef
  };
}